import { useEffect, useState, useRef } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { CalendarToday } from '@material-ui/icons'
import styled, { css } from 'styled-components'

import { showDateFormat, convertLongDate, showMonthYearFormat } from '@utils'
import { Devices, Spacings, Text } from '@styles'
import { ErrorMessage, FormElementContainer, Label, baseInputStyles } from '../../styled'

const Wrapper = styled.div`
	position: relative;
	height: fit-content;
	width: 100%;
`

// input + calendar icon, so the icon stays on the input when an error shows below it
const InputBox = styled.div`
	position: relative;
`

const DateInput = styled.input`
	${baseInputStyles}
	caret-color: transparent;
	cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};
	padding-right: ${Spacings.SPACING_10B};
`

const MobileOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	transition: all 100ms cubic-bezier(0, 0, 0.2, 1);
	z-index: 99;
	background: ${({ theme }) => theme.colors.surface.page};
	visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
	width: 100vw;
	height: 100vh;

	@media ${Devices.mobile} {
		display: none;
	}
`

const getPosition = ({ position }) => {
	switch (position) {
		case 'top':
			return css`
				bottom: 100%;
				left: 0;
			`
		case 'bottom':
		default:
			return css`
				top: 100%;
				left: 0;
			`
	}
}
const getTriangleStyles = ({ position, theme }) => {
	switch (position) {
		case 'top':
			return css`
				left: ${Spacings.SPACING_5B};
				bottom: -${Spacings.SPACING_2B};
				border-left: ${Spacings.SPACING_3B} solid transparent;
				border-right: ${Spacings.SPACING_3B} solid transparent;
				border-top: ${Spacings.SPACING_2B} solid ${theme.colors.brand.primary};
			`
		case 'bottom':
		default:
			return css`
				left: ${Spacings.SPACING_5B};
				top: -${Spacings.SPACING_2B};
				border-left: ${Spacings.SPACING_3B} solid transparent;
				border-right: ${Spacings.SPACING_3B} solid transparent;
				border-bottom: ${Spacings.SPACING_2B} solid ${theme.colors.brand.primary};
			`
	}
}

const InlineDatePickerWrapper = styled.div`
	position: fixed;
	bottom: 0;
	left: 0;
	width: 100vw;
	z-index: 100;

	& .react-datepicker {
		width: 100%;
		font-family: inherit;
		box-shadow: ${({ theme }) => theme.shadows.md};
		border: none;

		.react-datepicker__navigation-icon::before {
			border-color: ${({ theme }) => theme.colors.text.inverse};
			border-style: solid;
			border-width: ${Spacings.SPACING_2} ${Spacings.SPACING_2} 0 0;
			content: '';
			display: block;
			position: absolute;
			top: ${Spacings.SPACING_4B};
			height: ${Spacings.SPACING_3B};
			width: ${Spacings.SPACING_3B};
		}
		.react-datepicker__month-container {
			width: 100%;
			.react-datepicker__header {
				width: 100%;
				padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_2B} ${Spacings.SPACING_2B};
				background: ${({ theme }) => theme.colors.brand.primary};
				border-bottom: none;
				.react-datepicker__current-month {
					font-size: ${Text.X_LARGE};
					color: ${({ theme }) => theme.colors.text.inverse};
					font-weight: ${Text.BOLD_600};
					margin-bottom: ${Spacings.SPACING_2B};
				}
				.react-datepicker__day-name {
					font-size: ${Text.LARGE};
					color: ${({ theme }) => theme.colors.text.inverse};
					margin: ${Spacings.SPACING_2B};
				}
			}
			.react-datepicker__month {
				.react-datepicker__day {
					font-size: ${Text.LARGE};
					margin: ${Spacings.SPACING_2B};
					color: ${({ theme }) => theme.colors.text.primary};
				}
				.react-datepicker__day--selected {
					background: ${({ theme }) => theme.colors.brand.primary};
					color: ${({ theme }) => theme.colors.text.inverse};
				}
				.react-datepicker__day--disabled {
					color: ${({ theme }) => theme.colors.text.disabled};
				}
			}
		}
	}

	@media ${Devices.mobile} {
		position: absolute;
		left: initial;
		bottom: initial;
		width: fit-content;
		${getPosition}

		& .react-datepicker {
			.react-datepicker__triangle {
				display: static;
				position: absolute;
				box-shadow: ${({ theme }) => theme.shadows.md};
				${getTriangleStyles}
			}

			.react-datepicker__navigation-icon::before {
				height: ${Spacings.SPACING_2B};
				top: ${Spacings.SPACING_3B};
				width: ${Spacings.SPACING_2B};
			}
			.react-datepicker__month-container {
				border: 1px solid ${({ theme }) => theme.colors.brand.primary};
				border-radius: ${({ theme }) => theme.radius.md};
				overflow: hidden;

				.react-datepicker__header {
					.react-datepicker__current-month {
						font-size: ${Text.LARGE};
					}
					.react-datepicker__day-name {
						font-size: ${Text.MEDIUM};
						margin: ${Spacings.SPACING_1B};
					}
				}
				.react-datepicker__month {
					.react-datepicker__day {
						font-size: ${Text.MEDIUM};
						margin: ${Spacings.SPACING_1B};
					}
				}
			}
		}
	}
`

const IconWrapper = styled.div`
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;
	height: ${Spacings.SPACING_10B};
	pointer-events: none;
	bottom: 0;
	right: ${Spacings.SPACING_3B};

	& > svg {
		font-size: ${Text.LARGE};
		fill: ${({ selected, theme }) => (selected ? theme.colors.brand.primary : theme.colors.text.secondary)};
		transition: fill 150ms ease-in;
	}
`

function Calendar({
	id,
	position = 'bottom',
	label,
	placeholder,
	value,
	minDate,
	maxDate,
	onChange,
	disabled,
	endOfDay = true,
	readOnly,
	showMonthYearPicker = false,
	isSearch = false,
	showMonthDropdown = true,
	showYearDropdown = true,
	showMonthOnly = false,
	// When true: no future month/date. Caps maxDate at today and turns off the
	// month/year dropdowns so navigation is arrow-only (react-datepicker disables the
	// forward arrow at the max month) → future months can't be reached or selected.
	disableFutureMonth = false,
	// shown below the input (and turns its border red), same as Input's error
	error,
	...rest
}) {
	const effectiveMaxDate = disableFutureMonth ? maxDate ?? new Date() : maxDate
	const effectiveShowMonthDropdown = disableFutureMonth ? false : showMonthDropdown
	const effectiveShowYearDropdown = disableFutureMonth ? false : showYearDropdown
	const [showDatePicker, setShowDatePicker] = useState(false)
	const [selectedDate, setSelectedDate] = useState(convertLongDate(new Date(), endOfDay, isSearch))

	const datePickerRefInput = useRef()
	const datePickerRefWrapper = useRef()

	useEffect(() => {
		if (typeof value === 'number') {
			const val = new Date(value)
			setSelectedDate(val)
		} else if (value instanceof Date) {
			setSelectedDate(value)
		}
	}, [value])

	useEffect(() => {
		const handler = (e) => {
			const target = e.target
			if (datePickerRefInput.current && datePickerRefInput.current.contains(target)) {
				return
			}
			if (datePickerRefWrapper.current && datePickerRefWrapper.current.contains(target)) {
				return
			} else {
				setShowDatePicker(false)
			}
		}

		if (showDatePicker) {
			window.addEventListener('mousedown', handler)
		} else {
			window.removeEventListener('mousedown', handler)
		}

		return () => {
			window.removeEventListener('mousedown', handler)
		}
	}, [showDatePicker])

	const toggleDatePicker = () => {
		setShowDatePicker(!showDatePicker)
	}

	const changeHandler = (date) => {
		setShowDatePicker(false)
		setSelectedDate(date)

		const _d = convertLongDate(date, endOfDay, isSearch)

		if (typeof onChange === 'function') {
			onChange(_d.getTime())
		}
	}

	return (
		<>
			<MobileOverlay show={showDatePicker} />
			<Wrapper ref={(_ref) => (datePickerRefInput.current = _ref)}>
				<FormElementContainer>
					{label && <Label htmlFor={id} >{label}</Label>}
					<InputBox>
						<DateInput
							id={id}
							value={value ? (showMonthOnly ? showMonthYearFormat(selectedDate) : showDateFormat(selectedDate)) : '-'}
							type='text'
							onClick={toggleDatePicker}
							placeholder={placeholder ?? 'Select date'}
							disabled={disabled}
							readOnly
							$hasError={!!error}
							{...rest}
						/>
						{!readOnly && (
							<IconWrapper selected={showDatePicker}>
								<CalendarToday />
							</IconWrapper>
						)}
					</InputBox>
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</FormElementContainer>
				{showDatePicker && (
					<InlineDatePickerWrapper position={position} ref={(_ref) => (datePickerRefWrapper.current = _ref)}>
						<DatePicker
							inline
							disabledKeyboardNavigation
							minDate={minDate}
							maxDate={effectiveMaxDate}
							selected={selectedDate}
							onChange={changeHandler}
							showMonthYearPicker={showMonthYearPicker}
							showMonthDropdown={effectiveShowMonthDropdown}
							showYearDropdown={effectiveShowYearDropdown}
							dropdownMode='select'
						/>
					</InlineDatePickerWrapper>
				)}
			</Wrapper>
		</>
	)
}

export default Calendar
